<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :history="history"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
        @load-history="onLoadHistory"
        @start-history-autosave="startHistoryAutosave"
        @stop-history-autosave="stopHistoryAutosave"
    >
        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>
        <template #fields="{ loading }">
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="8" cols="12">
                    <field-string ref="name" v-model="form.name" :disabled="loading" :label="$t('name')" required />
                </v-col>
                <v-col md="4" cols="12">
                    <field-string ref="gi_ref" v-model="form.gi_ref" :disabled="loading" :label="$t('gi_ref')" />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="3" cols="12">
                    <field-fund-status ref="status" v-model="form.status" :disabled="loading" required />
                </v-col>
                <v-col md="3" cols="12">
                    <field-fund-type ref="type" v-model="form.type" :disabled="loading" required />
                </v-col>
                <v-col md="3" cols="12">
                    <field-tax-status ref="tax_status" v-model="form.tax_status" :disabled="loading" required />
                </v-col>
                <v-col md="3" cols="12">
                    <field-enum
                        ref="sdr_label"
                        v-model="form.sdr_label"
                        :disabled="loading"
                        :enums="SDRLabelEnum"
                        enum-a11y-key="enums.sdr_label"
                        :label="$t('sdr_label')"
                    />
                </v-col>
            </v-row>

            <field-description
                ref="summary"
                v-model="form.summary"
                :disabled="loading"
                :label="$t('summary')"
                wysiwyg
            />
            <field-description
                ref="overview"
                v-model="form.overview"
                :disabled="loading"
                :label="$t('overview')"
                wysiwyg
            />
            <field-toggle
                ref="esg_qualifying"
                v-model="form.esg_qualifying"
                :disabled="loading"
                :label="$t('does_this_product_qualify_as_an_esg_investment_product')"
                :type="'checkbox'"
            />
            <field-toggle
                ref="discretionary"
                v-model="form.discretionary"
                :disabled="loading"
                :label="$t('discretionary')"
                :type="'checkbox'"
                required
                :default-value="null"
            />
            <field-date ref="launch_at" v-model="form.launch_at" :disabled="loading" :label="$t('launch_at')" />
            <field-date
                ref="close_at"
                v-model="form.close_at"
                :disabled="loading"
                :label="$t('close_at')"
                preset-time="00:00:00"
            />
            <field-iht-type ref="iht_type" v-model="form.iht_type" :disabled="loading" />
            <field-toggle
                ref="iht_relief"
                v-model="form.iht_relief"
                :disabled="loading"
                :label="$t('does_this_product_qualify_for_iht_relief')"
                :type="'checkbox'"
            />
            <field-number
                ref="deployment_timeline"
                v-model="form.deployment_timeline"
                :disabled="loading"
                :label="$t('deployment_timeline') + ' (days)'"
            />
            <field-toggle
                ref="deployment_by_yearend"
                v-model="form.deployment_by_yearend"
                :disabled="loading"
                :label="$t('deployment_by_yearend')"
                :type="'checkbox'"
            />
            <field-description
                ref="description"
                v-model="form.description"
                :disabled="loading"
                :label="$t('description')"
                wysiwyg
            />
            <field-percentage
                ref="target_return"
                v-model="form.target_return"
                :disabled="loading"
                :label="$t('target_return')"
            />
            <field-description
                ref="exit_strategy"
                v-model="form.exit_strategy"
                :disabled="loading"
                :label="$t('exit_strategy')"
                wysiwyg
            />
            <field-money
                ref="funds_raised"
                v-model="form.funds_raised"
                :disabled="loading"
                :label="$t('funds_raised')"
            />
            <field-fund-manager ref="fund_manager_id" v-model="form.fund_manager_id" :disabled="loading" allow-create />
            <field-product-provider ref="provider_id" v-model="form.provider_id" :disabled="loading" allow-create />
            <field-nominee ref="nominee_id" v-model="form.nominee_id" :disabled="loading" allow-create />
            <field-custodian ref="custodian_id" v-model="form.custodian_id" :disabled="loading" allow-create />
            <field-fee ref="fees_id" v-model="form.fees_id" :disabled="loading" allow-create />
            <field-lookup
                ref="fund_structure_id"
                v-model="form.fund_structure_id"
                type="fund-structure"
                :disabled="loading"
                :label="$t('fund_structure')"
            />
            <field-lookup
                ref="investment_strategy_id"
                v-model="form.investment_strategy_id"
                type="investment-strategy"
                :disabled="loading"
                :label="$t('investment_strategy')"
            />
            <field-lookup
                ref="iht_sector_id"
                v-model="form.iht_sector_id"
                type="iht-sector"
                :disabled="loading"
                :label="$t('iht_sector')"
                @input:object="form.iht_sector = $event"
            />
        </template>
        <template v-if="prependCancelButton" #prepend-actions>
            <app-button-cancel @click="onCancel" />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import history from '@/mixins/history';
import { Fund } from '@/models';
import SDRLabelEnum from '@/enums/SDRLabel';

export default {
    name: 'CreateFund',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/forms/Form'
            ),
        FieldFundType: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/FundType'
            ),
        FieldFundStatus: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/FundStatus'
            ),
        FieldIhtType: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/IhtType'
            ),
        FieldTaxStatus: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/TaxStatus'
            ),
        FieldFundManager: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/FundManager'
            ),
        FieldProductProvider: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/ProductProvider'
            ),
        FieldNominee: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Nominee'
            ),
        FieldCustodian: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Custodian'
            ),
        FieldFee: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Fee'
            )
    },
    mixins: [form, history],
    data: () => ({
        id: 'CreateFund',
        model: Fund,
        SDRLabelEnum
    })
};
</script>
